import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import { comicCategories, getComicsByCategory, getCategoryBySlug } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'rexue');
  const [comics, setComics] = useState<Comic[]>([]);
  const category = getCategoryBySlug(activeSlug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    setComics(getComicsByCategory(activeSlug));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category?.name || ''}漫画 - 汗汗漫画`,
    description: `汗汗漫画${category?.name || ''}分类，收录${category?.count || 0}部${category?.name || ''}漫画作品，下拉式连续阅读，滚动观看不间断。`,
    url: `https://hanhancomic.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '汗汗漫画',
      url: 'https://hanhancomic.com',
    },
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '漫画分类' },
    { label: category?.name || '' },
  ];

  return (
    <>
      <SEO
        title={`${category?.name || ''}漫画 - 汗汗漫画 | 下拉式连续阅读`}
        description={`汗汗漫画${category?.name || ''}分类，收录${category?.count || 0}部${category?.name || ''}漫画作品，下拉式连续阅读，滚动观看不间断，免费流畅观看。`}
        keywords={`汗汗漫画,${category?.name || ''}漫画,下拉式漫画,连续阅读,在线阅读,免费漫画`}
        canonicalUrl={`https://hanhancomic.com/category/${activeSlug}`}
        jsonLd={categoryJsonLd}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
              {category?.name}漫画
            </h1>
            <p className="text-sm text-foreground-500 mb-8">
              共 {category?.count} 部作品，下拉式连续阅读体验
            </p>

            <div className="flex flex-wrap gap-2 mb-10">
              {comicCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => {
                    setActiveSlug(cat.slug);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`px-4 py-2 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50 border-primary-500'
                      : 'bg-background-50 text-foreground-600 border-background-200 hover:border-primary-400 hover:text-primary-500'
                  }`}
                >
                  {cat.name}
                  <span className={`ml-1.5 text-xs ${activeSlug === cat.slug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                    {cat.count}
                  </span>
                </Link>
              ))}
            </div>

            {comics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {comics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.slug}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-3">
                      <img
                        src={comic.coverUrl}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <span className="text-background-50 text-xs whitespace-nowrap">{comic.status}</span>
                      </div>
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs rounded-full font-medium whitespace-nowrap">
                          {comic.rating}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs text-foreground-500 whitespace-nowrap">{comic.author}</span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{comic.updateDate}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无漫画作品</p>
                <Link to="/category/rexue" className="text-sm text-primary-500 hover:text-primary-600 mt-2 inline-block whitespace-nowrap">
                  浏览其他分类
                </Link>
              </div>
            )}
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}